import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../../../utils/supabase';
import {
  addDays, centsToInput, computeInvoice, invoiceCall, invoiceDocument, invoiceStatus, isoToday, loadInvoice, METHODS,
  money, saveDraft, shortDate, STATUS_TONES, toBase64, toCents, UNITS,
} from '../../../utils/invoices';
import { buildInvoicePdf } from '../../../utils/invoicePdf';
import InvoiceDocument from '../../invoices/InvoiceDocument';
import { errorBox, inputClass, noticeBox } from '../../AuthLayout';
import { btnPrimary, btnSecondary, Card, Field } from './InvoiceSetup';

const newLine = (defaults) => ({ key: crypto.randomUUID(), product_id: '', description: '', quantity: '1', unit: 'each', price: '', taxable: true, product_line: '', tax_rate_ids: defaults });

async function downloadPdf(doc) {
  const bytes = await buildInvoicePdf(doc);
  const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = `Invoice-${doc.number || 'draft'}.pdf`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

/** Admin → Invoices → one invoice: edit a draft, or view / send / get paid on a sent one. */
export default function InvoiceEditor({ id, autoSend, seller, clients, products, taxRates, onClose, onOpen, onChanged }) {
  const [state, setState] = useState(id === 'new' ? 'new' : null); // null = loading
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  const reload = async () => {
    try { setData(await loadInvoice(id)); setState('loaded'); } catch (e) { setError(e.message); }
  };
  useEffect(() => { if (id !== 'new') reload(); /* eslint-disable-next-line */ }, [id]);

  if (error) return <div className={errorBox}>{error}</div>;
  if (state === null) return <p className="text-ink-muted">Loading invoice…</p>;
  if (state === 'new' || data?.inv.status === 'draft') {
    return <DraftEditor autoSend={autoSend} seller={seller} clients={clients} products={products} taxRates={taxRates} existing={data}
      onClose={onClose} onSaved={(newId, send) => { onChanged(); if (newId !== id) onOpen(newId, send); else reload(); }} />;
  }
  return <SentInvoice data={data} seller={seller} onClose={onClose} onReload={() => { reload(); onChanged(); }} onOpen={onOpen} onChanged={onChanged} />;
}

/* ------------------------------------------------------------------ draft */

function DraftEditor({ autoSend, seller, clients, products, taxRates, existing, onClose, onSaved }) {
  const activeRates = taxRates.filter((t) => !t.archived);
  const defaults = activeRates.filter((t) => t.is_default).map((t) => t.id);
  const rateMap = Object.fromEntries(taxRates.map((t) => [t.id, t]));
  const inv = existing?.inv;
  const [form, setForm] = useState(() => ({
    client_id: inv?.client_id || '',
    issue_date: inv?.issue_date || isoToday(),
    due_date: inv?.due_date || addDays(isoToday(), seller.default_terms_days),
    po_number: inv?.po_number || '',
    notes: inv?.notes ?? seller.invoice_notes ?? '',
    terms: inv?.terms || '',
    discount_type: inv?.discount_type || '',
    discount_value: inv?.discount_value ? String(inv.discount_value) : '',
    tapzo_access: inv?.tapzo_access || 'none',
    tapzo_access_until: inv?.tapzo_access_until || addDays(isoToday(), 365),
  }));
  const [lines, setLines] = useState(() => (existing?.lines?.length
    ? existing.lines.map((l) => ({
        key: l.id, product_id: l.product_id || '', description: l.description, quantity: String(Number(l.quantity)), unit: l.unit || 'each',
        price: centsToInput(l.unit_price_cents), taxable: l.taxable, product_line: l.product_line || '',
        tax_rate_ids: (l.taxes || []).length ? l.taxes.map((t) => t.tax_rate_id) : (l.taxable ? defaults : []),
      }))
    : [newLine(defaults)]));
  const [preview, setPreview] = useState(false);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [sending, setSending] = useState(!!autoSend && !!existing);

  const client = clients.find((c) => c.id === form.client_id);
  const set = (k) => (e) => {
    const v = e.target.value;
    setForm((f) => {
      const next = { ...f, [k]: v };
      if (k === 'client_id' || k === 'issue_date') {
        const c = clients.find((x) => x.id === (k === 'client_id' ? v : f.client_id));
        next.due_date = addDays(k === 'issue_date' ? v : f.issue_date, c?.terms_days ?? seller.default_terms_days);
      }
      return next;
    });
  };
  const setLine = (key, patch) => setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  const pickProduct = (key, productId) => {
    const p = products.find((x) => x.id === productId);
    if (!p) return setLine(key, { product_id: '' });
    setLine(key, {
      product_id: p.id, description: p.description ? `${p.name}\n${p.description}` : p.name, unit: p.unit,
      price: centsToInput(p.unit_price_cents), taxable: p.taxable, product_line: p.product_line, tax_rate_ids: p.taxable ? defaults : [],
    });
  };

  const totals = useMemo(() => computeInvoice({
    lines: lines.map((l) => ({ ...l, quantity: Number(l.quantity || 0), unit_price_cents: toCents(l.price) })),
    discountType: form.discount_type || null, discountValue: form.discount_value, taxRates: rateMap, exempt: !!client?.tax_exempt,
  }), [lines, form.discount_type, form.discount_value, client, rateMap]);

  const record = () => ({
    ...(inv ? { id: inv.id } : { seller_id: seller.id }),
    client_id: form.client_id, issue_date: form.issue_date, due_date: form.due_date, po_number: form.po_number || null,
    notes: form.notes || null, terms: form.terms || null, discount_type: form.discount_type || null,
    discount_value: Number(form.discount_value || 0), subtotal_cents: totals.subtotal, discount_cents: totals.discount,
    tax_cents: totals.tax, total_cents: totals.total, tapzo_access: client?.org_id ? form.tapzo_access : 'none',
    tapzo_access_until: client?.org_id && form.tapzo_access !== 'none' ? form.tapzo_access_until : null,
  });
  const lineRecords = () => totals.lines.map((l) => ({ ...l, product_line: l.product_line || products.find((p) => p.id === l.product_id)?.product_line || 'Other' }));

  const validate = () => {
    if (!form.client_id) return 'Choose a client.';
    if (!lines.some((l) => l.description.trim())) return 'Add at least one line with a description.';
    if (form.due_date < form.issue_date) return 'The due date is before the issue date.';
    return '';
  };
  const save = async () => {
    const problem = validate();
    if (problem) { setError(problem); return null; }
    setBusy('save'); setError('');
    try {
      const newId = await saveDraft(record(), lineRecords());
      return newId;
    } catch (e) {
      setError(e.message);
      return null;
    } finally {
      setBusy('');
    }
  };
  const draftDoc = invoiceDocument({
    inv: { ...record(), number: null, status: 'draft', paid_cents: 0 }, lines: totals.lines, seller, client,
  });

  const removeDraft = async () => {
    if (!inv || !window.confirm('Delete this draft?')) return;
    const { error: err } = await supabase.from('invoices').delete().eq('id', inv.id);
    if (err) setError(err.message); else onClose(true);
  };

  if (sending && inv) {
    return <SendPanel invoiceId={inv.id} client={client} draft onDone={() => { setSending(false); onSaved(inv.id); }} onCancel={() => setSending(false)} />;
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <button type="button" onClick={() => onClose()} className="text-sm font-medium text-ink-muted hover:text-ink">← All invoices</button>
        <h2 className="text-xl font-bold flex-1">{inv ? 'Draft invoice' : 'New invoice'}</h2>
        <button type="button" className={btnSecondary} onClick={() => setPreview((p) => !p)}>{preview ? 'Edit' : 'Preview'}</button>
        <button type="button" className={btnSecondary} onClick={() => downloadPdf(draftDoc)}>Download PDF</button>
        <button type="button" className={btnSecondary} disabled={!!busy} onClick={async () => { const nid = await save(); if (nid) onSaved(nid); }}>
          {busy === 'save' ? 'Saving…' : 'Save draft'}
        </button>
        <button type="button" className={btnPrimary} disabled={!!busy} onClick={async () => {
          const nid = await save();
          if (!nid) return;
          if (!inv || nid !== inv.id) { onSaved(nid, true); return; }
          setSending(true);
        }}>
          {inv ? 'Send…' : 'Save, then send'}
        </button>
      </div>
      {error && <div className={errorBox}>{error}</div>}

      {preview ? <InvoiceDocument doc={draftDoc} /> : (
        <>
          <Card title="Client and dates">
            <div className="grid sm:grid-cols-4 gap-4">
              <Field label="Client" className="sm:col-span-2">
                <select className={inputClass} value={form.client_id} onChange={set('client_id')}>
                  <option value="">Choose a client…</option>
                  {clients.filter((c) => !c.archived || c.id === form.client_id).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </Field>
              <Field label="Issue date"><input type="date" className={inputClass} value={form.issue_date} onChange={set('issue_date')} /></Field>
              <Field label="Due date"><input type="date" className={inputClass} value={form.due_date} onChange={set('due_date')} /></Field>
              <Field label="PO number (optional)"><input className={inputClass} value={form.po_number} onChange={set('po_number')} /></Field>
            </div>
            {clients.length === 0 && <p className="text-sm text-ink-muted">No clients yet: add one in the Clients tab first.</p>}
            {client?.tax_exempt && <p className="text-sm font-semibold text-blue">This client is tax exempt: no sales tax will be charged.</p>}
          </Card>

          <Card title="Lines">
            <div className="space-y-4">
              {lines.map((l, idx) => {
                const calc = totals.lines[idx];
                return (
                  <div key={l.key} className="rounded-xl border border-line p-3 space-y-3">
                    <div className="grid sm:grid-cols-[1.2fr_2fr] gap-3">
                      <Field label="Product">
                        <select className={inputClass} value={l.product_id} onChange={(e) => pickProduct(l.key, e.target.value)}>
                          <option value="">Custom line</option>
                          {products.filter((p) => !p.archived || p.id === l.product_id).map((p) => <option key={p.id} value={p.id}>{p.name} · {money(p.unit_price_cents)}/{p.unit}</option>)}
                        </select>
                      </Field>
                      <Field label="Description"><textarea rows={2} className={inputClass} value={l.description} onChange={(e) => setLine(l.key, { description: e.target.value })} /></Field>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-[110px_130px_150px_1fr_auto] gap-3 items-end">
                      <Field label="Qty"><input inputMode="decimal" className={inputClass} value={l.quantity} onChange={(e) => setLine(l.key, { quantity: e.target.value })} /></Field>
                      <Field label="Per">
                        <select className={inputClass} value={l.unit} onChange={(e) => setLine(l.key, { unit: e.target.value })}>{UNITS.map((u) => <option key={u}>{u}</option>)}</select>
                      </Field>
                      <Field label="Price (USD)"><input inputMode="decimal" className={inputClass} value={l.price} onChange={(e) => setLine(l.key, { price: e.target.value })} placeholder="0.00" /></Field>
                      <div className="text-sm space-y-1.5 col-span-2 sm:col-span-1">
                        <label className="flex items-center gap-2"><input type="checkbox" checked={l.taxable} onChange={(e) => setLine(l.key, { taxable: e.target.checked, tax_rate_ids: e.target.checked ? defaults : [] })} /> Taxable</label>
                        {l.taxable && (
                          <div className="flex flex-wrap gap-x-3 gap-y-1">
                            {activeRates.map((t) => (
                              <label key={t.id} className="flex items-center gap-1.5 text-ink-muted">
                                <input type="checkbox" checked={l.tax_rate_ids.includes(t.id)}
                                  onChange={(e) => setLine(l.key, { tax_rate_ids: e.target.checked ? [...l.tax_rate_ids, t.id] : l.tax_rate_ids.filter((x) => x !== t.id) })} />
                                {t.name} {Number(t.rate)}%
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="text-right col-span-2 sm:col-span-1">
                        <div className="font-semibold tabular-nums">{money(calc?.amount_cents)}</div>
                        {calc?.tax_cents > 0 && <div className="text-xs text-ink-muted tabular-nums">+ {money(calc.tax_cents)} tax</div>}
                        {lines.length > 1 && <button type="button" onClick={() => setLines((ls) => ls.filter((x) => x.key !== l.key))} className="text-xs text-coral-ink hover:underline mt-1">Remove</button>}
                      </div>
                    </div>
                  </div>
                );
              })}
              <button type="button" className={btnSecondary} onClick={() => setLines((ls) => [...ls, newLine(defaults)])}>Add line</button>
            </div>
            <div className="flex flex-wrap items-end justify-between gap-6 border-t border-line pt-4">
              <div className="flex gap-3 items-end">
                <Field label="Discount">
                  <select className={inputClass} value={form.discount_type} onChange={set('discount_type')}>
                    <option value="">None</option><option value="percent">Percent</option><option value="amount">Amount ($)</option>
                  </select>
                </Field>
                {form.discount_type && <Field label={form.discount_type === 'percent' ? '%' : 'USD'}><input inputMode="decimal" className={`${inputClass} w-28`} value={form.discount_value} onChange={set('discount_value')} /></Field>}
              </div>
              <dl className="grid grid-cols-[auto_auto] gap-x-6 gap-y-1 tabular-nums text-sm">
                <dt className="text-ink-muted text-right">Subtotal</dt><dd className="text-right">{money(totals.subtotal)}</dd>
                {totals.discount > 0 && (<><dt className="text-ink-muted text-right">Discount</dt><dd className="text-right">-{money(totals.discount)}</dd></>)}
                <dt className="text-ink-muted text-right">Sales tax</dt><dd className="text-right">{money(totals.tax)}</dd>
                <dt className="font-bold text-right">Total</dt><dd className="font-bold text-right">{money(totals.total)}</dd>
              </dl>
            </div>
          </Card>

          <Card title="Notes and terms">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Notes (on the invoice)"><textarea rows={3} className={inputClass} value={form.notes} onChange={set('notes')} /></Field>
              <Field label="Terms (on the invoice)"><textarea rows={3} className={inputClass} value={form.terms} onChange={set('terms')} placeholder={`Payment due within ${client?.terms_days ?? seller.default_terms_days} days.`} /></Field>
            </div>
          </Card>

          {client?.org_id && (
            <Card title="Carduzo access">
              <p className="text-sm text-ink-muted">This client is linked to the Carduzo account <strong>{client.organizations?.name}</strong>. The invoice can switch on their Carduzo access (free access until the date below). Accounts paying through Stripe are never changed.</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Switch on Carduzo access">
                  <select className={inputClass} value={form.tapzo_access} onChange={set('tapzo_access')}>
                    <option value="none">No</option><option value="on_send">When the invoice is sent</option><option value="on_paid">When the invoice is paid</option>
                  </select>
                </Field>
                {form.tapzo_access !== 'none' && <Field label="Access until"><input type="date" className={inputClass} value={form.tapzo_access_until} onChange={set('tapzo_access_until')} /></Field>}
              </div>
            </Card>
          )}
        </>
      )}
      {inv && <button type="button" onClick={removeDraft} className="text-sm text-coral-ink hover:underline">Delete this draft</button>}
    </div>
  );
}

/* ------------------------------------------------------------ send panel */

function SendPanel({ invoiceId, client, draft, onDone, onCancel }) {
  const [to, setTo] = useState((client?.emails || []).join(', '));
  const [message, setMessage] = useState('Here is your invoice. Thank you for your business!');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const send = async (e) => {
    e.preventDefault();
    const emails = to.split(/[,;\s]+/).map((x) => x.trim()).filter(Boolean);
    if (!emails.length) return setError('Add at least one email address.');
    setBusy(true); setError('');
    try {
      // 1. Number + freeze (drafts only), 2. PDF from the final invoice, 3. email with the PDF
      if (draft) await invoiceCall('finalize', { invoice_id: invoiceId });
      const { inv, lines } = await loadInvoice(invoiceId);
      const bytes = await buildInvoicePdf(invoiceDocument({ inv, lines }));
      const res = await invoiceCall('email', {
        invoice_id: invoiceId, to: emails, message, pdf_base64: toBase64(bytes), pdf_name: `Invoice-${inv.number}.pdf`,
      });
      setResult(res.results);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };
  if (result) {
    return (
      <Card title="Invoice sent">
        <ul className="text-sm space-y-1">
          {result.map((r) => <li key={r.email}>{r.sent ? '✓ Sent to' : '✗ Not sent to'} {r.email}{r.error ? ` (${r.error})` : ''}</li>)}
        </ul>
        <p className="text-sm text-ink-muted">Delivery and opens show in Admin → Email log.</p>
        <button type="button" className={btnPrimary} onClick={onDone}>Done</button>
      </Card>
    );
  }
  return (
    <form onSubmit={send}>
      <Card title={draft ? 'Send this invoice' : 'Email this invoice again'}>
        {draft && <div className={noticeBox}>Sending gives the invoice its number and locks it. To change it later, void it and create a new one.</div>}
        <Field label="To" hint="Separate several emails with commas."><input className={inputClass} value={to} onChange={(e) => setTo(e.target.value)} /></Field>
        <Field label="Message"><textarea rows={3} className={inputClass} value={message} onChange={(e) => setMessage(e.target.value)} /></Field>
        <p className="text-sm text-ink-muted">The email includes a "View invoice" link, how to pay by check, and the invoice as a PDF.</p>
        {error && <div className={errorBox}>{error}</div>}
        <div className="flex gap-2.5">
          <button type="submit" disabled={busy} className={btnPrimary}>{busy ? 'Sending…' : draft ? 'Send invoice' : 'Send email'}</button>
          <button type="button" onClick={onCancel} disabled={busy} className={btnSecondary}>Cancel</button>
        </div>
      </Card>
    </form>
  );
}

/* ----------------------------------------------------------- sent invoice */

function SentInvoice({ data, seller, onClose, onReload, onOpen, onChanged }) {
  const { inv, lines, payments, events } = data;
  const doc = invoiceDocument({ inv, lines });
  const st = invoiceStatus(inv);
  const balance = inv.total_cents - inv.paid_cents;
  const [panel, setPanel] = useState(null); // 'email' | 'payment'
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const link = `${window.location.origin}/i/${inv.public_token}`;

  const voidIt = async () => {
    if (!window.confirm(`Void invoice ${inv.number}? It stays in your records as void and can't be paid.`)) return;
    const { error: err } = await supabase.from('invoices').update({ status: 'void' }).eq('id', inv.id);
    if (err) setError(err.message); else onReload();
  };
  const duplicate = async () => {
    try {
      const newId = await saveDraft({
        seller_id: inv.seller_id, client_id: inv.client_id, issue_date: isoToday(),
        due_date: addDays(isoToday(), inv.invoice_clients?.terms_days ?? seller.default_terms_days),
        po_number: null, notes: inv.notes, terms: inv.terms, discount_type: inv.discount_type, discount_value: inv.discount_value,
        subtotal_cents: inv.subtotal_cents, discount_cents: inv.discount_cents, tax_cents: inv.tax_cents, total_cents: inv.total_cents,
        tapzo_access: inv.tapzo_access, tapzo_access_until: inv.tapzo_access_until,
      }, lines);
      onChanged();
      onOpen(newId);
    } catch (e) { setError(e.message); }
  };
  const removePayment = async (p) => {
    if (!window.confirm(`Delete the ${money(p.amount_cents)} payment from ${shortDate(p.received_on)}?`)) return;
    const { error: err } = await supabase.from('invoice_payments').delete().eq('id', p.id);
    if (err) setError(err.message); else onReload();
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <button type="button" onClick={() => onClose()} className="text-sm font-medium text-ink-muted hover:text-ink">← All invoices</button>
        <h2 className="text-xl font-bold">Invoice {inv.number}</h2>
        <span className={`h-6 px-2.5 rounded-full text-xs font-semibold inline-flex items-center ${STATUS_TONES[st.tone]}`}>{st.label}</span>
        <span className="flex-1" />
        <button type="button" className={btnSecondary} onClick={() => downloadPdf(doc)}>Download PDF</button>
        <button type="button" className={btnSecondary} onClick={async () => { try { await navigator.clipboard.writeText(link); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { window.prompt('Invoice link', link); } }}>{copied ? 'Copied' : 'Copy link'}</button>
        <button type="button" className={btnSecondary} onClick={duplicate}>Duplicate</button>
        {inv.status !== 'void' && <button type="button" className={btnSecondary} onClick={() => setPanel('email')}>Email…</button>}
        {['sent', 'partial'].includes(inv.status) && <button type="button" className={btnPrimary} onClick={() => setPanel('payment')}>Record payment</button>}
      </div>
      {error && <div className={errorBox}>{error}</div>}
      {panel === 'email' && <SendPanel invoiceId={inv.id} client={{ emails: inv.client_snapshot?.emails || inv.invoice_clients?.emails }} onDone={() => { setPanel(null); onReload(); }} onCancel={() => setPanel(null)} />}
      {panel === 'payment' && <PaymentForm inv={inv} balance={balance} onDone={() => { setPanel(null); onReload(); }} onCancel={() => setPanel(null)} />}

      <div className="grid lg:grid-cols-[minmax(0,1fr)_320px] gap-5 items-start">
        <InvoiceDocument doc={doc} />
        <div className="space-y-5">
          <Card title="Payments">
            {payments.length === 0 ? <p className="text-sm text-ink-muted">No payments yet.</p> : (
              <ul className="text-sm divide-y divide-line">
                {payments.map((p) => (
                  <li key={p.id} className="py-2 flex items-start gap-2">
                    <div className="flex-1">
                      <div className="font-semibold tabular-nums">{money(p.amount_cents)}</div>
                      <div className="text-ink-muted">{shortDate(p.received_on)} · {METHODS[p.method]}{p.reference ? ` #${p.reference}` : ''}{p.deposited_on ? ` · deposited ${shortDate(p.deposited_on)}` : ''}</div>
                      {p.note && <div className="text-ink-muted">{p.note}</div>}
                    </div>
                    {inv.status !== 'void' && <button type="button" onClick={() => removePayment(p)} className="text-xs text-coral-ink hover:underline">Delete</button>}
                  </li>
                ))}
              </ul>
            )}
            <div className="text-sm flex justify-between border-t border-line pt-2"><span className="text-ink-muted">Balance due</span><strong className="tabular-nums">{money(balance)}</strong></div>
          </Card>
          <Card title="History">
            <ul className="text-sm space-y-1.5">
              <li className="text-ink-muted">Created {shortDate(inv.created_at)}</li>
              {events.map((e) => (
                <li key={e.id} className="text-ink-muted">
                  {shortDate(e.created_at)} · {{ sent: 'Sent (numbered)', emailed: `Emailed to ${(e.detail?.to || []).join(', ')}`, receipt_emailed: `Receipt emailed to ${(e.detail?.to || []).join(', ')}`, viewed: 'Viewed by the client', payment: `Payment ${money(e.detail?.amount_cents)}`, access: `Carduzo access on until ${e.detail?.until}` }[e.kind] || e.kind}
                </li>
              ))}
              {inv.voided_at && <li className="text-coral-ink">Voided {shortDate(inv.voided_at)}</li>}
            </ul>
          </Card>
          {inv.status !== 'void' && inv.status !== 'paid' && (
            <button type="button" onClick={voidIt} className="text-sm text-coral-ink hover:underline">Void this invoice</button>
          )}
        </div>
      </div>
    </div>
  );
}

function PaymentForm({ inv, balance, onDone, onCancel }) {
  const [form, setForm] = useState({ received_on: isoToday(), amount: centsToInput(balance), method: 'check', reference: '', deposited_on: '', note: '', receipt: true });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  const save = async (e) => {
    e.preventDefault();
    const amount = toCents(form.amount);
    if (amount <= 0) return setError('Enter the amount received.');
    if (amount > balance && !window.confirm(`That's more than the balance (${money(balance)}). Record it anyway?`)) return;
    setBusy(true); setError('');
    const { data, error: err } = await supabase.from('invoice_payments').insert({
      invoice_id: inv.id, received_on: form.received_on, amount_cents: amount, method: form.method,
      reference: form.reference || null, deposited_on: form.deposited_on || null, note: form.note || null,
    }).select('id').single();
    if (err) { setBusy(false); return setError(err.message); }
    const to = inv.client_snapshot?.emails || [];
    if (form.receipt && to.length) {
      try { await invoiceCall('email', { invoice_id: inv.id, to, kind: 'receipt', payment_id: data.id }); } catch (e2) { setBusy(false); setError(`Payment saved, but the receipt wasn't sent: ${e2.message}`); return; }
    }
    setBusy(false);
    onDone();
  };
  return (
    <form onSubmit={save}>
      <Card title={`Record a payment for ${inv.number}`}>
        <div className="grid sm:grid-cols-3 gap-4">
          <Field label="Amount (USD)"><input inputMode="decimal" className={inputClass} value={form.amount} onChange={set('amount')} /></Field>
          <Field label="Received on"><input type="date" className={inputClass} value={form.received_on} onChange={set('received_on')} /></Field>
          <Field label="Method">
            <select className={inputClass} value={form.method} onChange={set('method')}>{Object.entries(METHODS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
          </Field>
          <Field label={form.method === 'check' ? 'Check number' : 'Reference'}><input className={inputClass} value={form.reference} onChange={set('reference')} /></Field>
          <Field label="Deposited on (optional)"><input type="date" className={inputClass} value={form.deposited_on} onChange={set('deposited_on')} /></Field>
          <Field label="Note (private)"><input className={inputClass} value={form.note} onChange={set('note')} /></Field>
        </div>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.receipt} onChange={set('receipt')} /> Email a "Payment received" receipt to {(inv.client_snapshot?.emails || []).join(', ') || 'the client (no email on file)'}</label>
        {error && <div className={errorBox}>{error}</div>}
        <div className="flex gap-2.5">
          <button type="submit" disabled={busy} className={btnPrimary}>{busy ? 'Saving…' : 'Save payment'}</button>
          <button type="button" onClick={onCancel} disabled={busy} className={btnSecondary}>Cancel</button>
        </div>
      </Card>
    </form>
  );
}
